import {
    ActionType,
    FranchiseTerritoryId,
    RegionId,
    rootRegionParentId,
} from "@sp-crm/core";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { CrmTable, CrmTableProps } from "components/table/crm-table";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { ReorderableStringList } from "components/ui/reorderable-string-list";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { useMessage } from "hooks/use-message";
import React from "react";
import { useSelector } from "react-redux";
import { ApplicationState } from "store/state";
import { useFeature, useIsAllowedInWholeTenant } from "store/selectors/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";

interface TerritoryFormData {
    code: string;
    zipCodes: string[];
    assignedRegionId: RegionId | "";
}

interface TerritoryRow {
    id: FranchiseTerritoryId;
    code: string;
    zipCodes: string[];
    assignedRegionId?: RegionId | null;
    assignedRegionName?: string | null;
    dateAdded: string;
    updatedAt: string;
}

interface TerritoryFormPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    onSuccess: () => void;
    editingTerritory?: TerritoryRow;
}

const TerritoryFormPanel: React.FC<TerritoryFormPanelProps> = props => {
    const { isOpen, onDismiss, onSuccess, editingTerritory } = props;

    const isEditing = !!editingTerritory;
    const [formData, setFormData] = React.useState<TerritoryFormData>({
        code: "",
        zipCodes: [],
        assignedRegionId: "",
    });
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const { message: panelMessage, setMessage: setPanelMessage } = useMessage();

    React.useEffect(() => {
        if (isOpen) {
            if (editingTerritory) {
                setFormData({
                    code: editingTerritory.code,
                    zipCodes: editingTerritory.zipCodes,
                    assignedRegionId: editingTerritory.assignedRegionId ?? "",
                });
            } else {
                setFormData({ code: "", zipCodes: [], assignedRegionId: "" });
            }
            setValidationError(null);
        }
    }, [isOpen, editingTerritory]);

    const regions = useSelector((state: ApplicationState) => state.region.regions);
    const childRegions = regions.filter(r => r.parentRegionId !== rootRegionParentId);

    const trpcClient = trpc.useTRPC();

    const createMutation = useMutation({
        ...trpcClient.franchiseTerritory.create.mutationOptions(),
        onSuccess: () => {
            onSuccess();
            onDismiss();
        },
        onError: (error: { message?: string }) => {
            setPanelMessage("error", error.message || "Failed to create territory.");
        },
    });

    const updateMutation = useMutation({
        ...trpcClient.franchiseTerritory.update.mutationOptions(),
        onSuccess: () => {
            onSuccess();
            onDismiss();
        },
        onError: (error: { message?: string }) => {
            setPanelMessage("error", error.message || "Failed to update territory.");
        },
    });

    const isPending = createMutation.isPending || updateMutation.isPending;

    const handleSubmit = React.useCallback(() => {
        if (!formData.code.trim()) {
            setValidationError("Territory code is required.");
            return;
        }
        if (formData.zipCodes.length === 0) {
            setValidationError("At least one zip code is required.");
            return;
        }
        setValidationError(null);

        if (isEditing && editingTerritory) {
            updateMutation.mutate({
                id: editingTerritory.id,
                code: formData.code,
                zipCodes: formData.zipCodes,
                assignedRegionId: formData.assignedRegionId || null,
            });
        } else {
            createMutation.mutate({
                code: formData.code,
                zipCodes: formData.zipCodes,
                ...(formData.assignedRegionId
                    ? { assignedRegionId: formData.assignedRegionId }
                    : {}),
            });
        }
    }, [formData, isEditing, editingTerritory, createMutation, updateMutation]);

    return (
        <Panel
            isOpen={isOpen}
            headerText={isEditing ? "Edit Territory" : "Add Territory"}
            onDismiss={onDismiss}
            type={PanelType.medium}>
            <div className="space-y-4">
                <Input
                    label="Territory Code"
                    value={formData.code}
                    onChange={e =>
                        setFormData(prev => ({
                            ...prev,
                            code: e.target.value,
                        }))
                    }
                    disabled={isPending}
                />
                <ReorderableStringList
                    label="Zip Codes"
                    initial={formData.zipCodes}
                    onCommit={values =>
                        setFormData(prev => ({ ...prev, zipCodes: values }))
                    }
                    addButtonLabel="Add zip code"
                />
                <Select
                    label="Assigned Region (optional)"
                    value={formData.assignedRegionId}
                    onChange={e =>
                        setFormData(prev => ({
                            ...prev,
                            assignedRegionId: e.target.value as RegionId | "",
                        }))
                    }
                    disabled={isPending}>
                    <option value="">No region</option>
                    {childRegions.map(region => (
                        <option key={region.id} value={region.id}>
                            {region.name}
                        </option>
                    ))}
                </Select>
                {validationError ? (
                    <p className="text-sm text-red-600">{validationError}</p>
                ) : null}
                {panelMessage ? (
                    <InlineBanner type={panelMessage.type}>
                        {panelMessage.content}
                    </InlineBanner>
                ) : null}
                <div className="flex space-x-2">
                    <PrimaryButton onClick={handleSubmit} disabled={isPending}>
                        {isPending ? "Saving..." : "Save"}
                    </PrimaryButton>
                    <SecondaryButton onClick={onDismiss} disabled={isPending}>
                        Cancel
                    </SecondaryButton>
                </div>
            </div>
        </Panel>
    );
};

export const AgencySettingsFranchise: React.FC<unknown> = () => {
    const isEnabled = useFeature("franchiseManagement");
    const canCreate = useIsAllowedInWholeTenant(ActionType.CreateFranchiseTerritory);
    const canUpdate = useIsAllowedInWholeTenant(ActionType.UpdateFranchiseTerritory);
    const canDelete = useIsAllowedInWholeTenant(ActionType.DeleteFranchiseTerritory);

    const trpcClient = trpc.useTRPC();
    const {
        data: territories,
        isLoading,
        refetch,
    } = useQuery(trpcClient.franchiseTerritory.list.queryOptions());

    const { message, setMessage } = useMessage();

    const deleteMutation = useMutation({
        ...trpcClient.franchiseTerritory.delete.mutationOptions(),
        onSuccess: () => {
            refetch();
        },
        onError: (error: { message?: string }) => {
            setMessage("error", error.message || "Failed to delete territory.");
        },
    });

    const [showPanel, setShowPanel] = React.useState(false);
    const [editingTerritory, setEditingTerritory] = React.useState<
        TerritoryRow | undefined
    >(undefined);

    const handleAdd = React.useCallback(() => {
        setEditingTerritory(undefined);
        setShowPanel(true);
    }, []);

    const handleEdit = React.useCallback((territory: TerritoryRow) => {
        setEditingTerritory(territory);
        setShowPanel(true);
    }, []);

    const handleDismiss = React.useCallback(() => {
        setShowPanel(false);
        setEditingTerritory(undefined);
    }, []);

    const handleSuccess = React.useCallback(() => {
        refetch();
    }, [refetch]);

    const columns: CrmTableProps<TerritoryRow>["columns"] = React.useMemo(
        () => [
            {
                key: "code",
                header: "Code",
            },
            {
                key: "zipCodes",
                header: "Zip Codes",
                renderCell: ({ record }) => (
                    <span className="text-sm">{record?.zipCodes.join(", ") ?? ""}</span>
                ),
            },
            {
                key: "assignedRegionName",
                header: "Assigned Region",
                renderCell: ({ record }) => <>{record?.assignedRegionName ?? ""}</>,
            },
            ...((canUpdate || canDelete
                ? [
                      {
                          key: "_actions" as keyof TerritoryRow,
                          header: "",
                          enableSorting: false,
                          renderCell: ({
                              record,
                          }: {
                              record: TerritoryRow | undefined;
                          }) =>
                              record ? (
                                  <div className="flex items-center space-x-2">
                                      {canUpdate ? (
                                          <EditButton
                                              backgroundColor="bg-white"
                                              onClick={() => handleEdit(record)}
                                          />
                                      ) : null}
                                      {canDelete ? (
                                          <DeleteButton
                                              backgroundColor="bg-white"
                                              confirm={{
                                                  title: "Delete Territory?",
                                                  message: `Are you sure you want to delete territory "${record.code}"?`,
                                                  confirmLabel: "Delete",
                                              }}
                                              onClick={() =>
                                                  deleteMutation.mutate({
                                                      id: record.id,
                                                  })
                                              }
                                          />
                                      ) : null}
                                  </div>
                              ) : null,
                      },
                  ]
                : []) as CrmTableProps<TerritoryRow>["columns"]),
        ],
        [canUpdate, canDelete, handleEdit, deleteMutation],
    );

    if (!isEnabled || !canUpdate) {
        return null;
    }

    return (
        <div className="space-y-4 lg:space-y-8">
            <ContentFull>
                <ContentHeader>Franchise Management</ContentHeader>
                {isLoading ? (
                    <Spinner />
                ) : (
                    <div className="space-y-4">
                        {canCreate ? (
                            <div>
                                <PrimaryButton onClick={handleAdd}>
                                    Add Territory
                                </PrimaryButton>
                            </div>
                        ) : null}
                        {message ? (
                            <InlineBanner type={message.type}>
                                {message.content}
                            </InlineBanner>
                        ) : null}
                        {territories && territories.length > 0 ? (
                            <CrmTable columns={columns} data={territories} />
                        ) : (
                            <p className="text-sm text-gray-500">
                                No franchise territories have been created yet.
                            </p>
                        )}
                    </div>
                )}
            </ContentFull>
            <TerritoryFormPanel
                isOpen={showPanel}
                onDismiss={handleDismiss}
                onSuccess={handleSuccess}
                editingTerritory={editingTerritory}
            />
        </div>
    );
};
